import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ActivityBucket {
  date: string; // yyyy-MM-dd (bucket start, viewer's calendar)
  points: number;
  rewards: number;
}

interface Props {
  buckets: ActivityBucket[];
  granularity: "day" | "month";
}

const CHART_HEIGHT = 160;

// Rounds the axis max up to 1/2/2.5/5 × 10^n so gridline labels are readable.
const niceMax = (value: number) => {
  if (value <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(value)));
  const f = value / exp;
  const step = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return step * exp;
};

const parseLocalDate = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const ActivityBarChart: React.FC<Props> = ({ buckets, granularity }) => {
  const { t, i18n } = useTranslation();
  const [hover, setHover] = useState<number | null>(null);

  const max = niceMax(Math.max(0, ...buckets.map(b => b.points)));
  const gridValues = [max, max / 2, 0];
  const locale = i18n.language === "ar" ? "ar" : "en-US";

  const formatDate = (s: string) =>
    parseLocalDate(s).toLocaleDateString(locale,
      granularity === "day" ? { month: "short", day: "numeric" } : { month: "short", year: "numeric" });

  const tickIndexes = buckets.length <= 1
    ? [0]
    : Array.from(new Set([0, Math.floor((buckets.length - 1) / 2), buckets.length - 1]));

  const gap = buckets.length > 60 ? 1 : 2;

  return (
    // Time runs left→right in both languages so the axis reads the same way everywhere.
    <div dir="ltr" className="w-full">
      <div className="flex gap-2">
        {/* Y axis labels */}
        <div className="relative flex-shrink-0 w-10 text-[10px] text-gray-400" style={{ height: CHART_HEIGHT }}>
          {gridValues.map(v => (
            <span
              key={v}
              className="absolute right-0 -translate-y-1/2 tabular-nums"
              style={{ top: `${(1 - v / max) * 100}%` }}
            >
              {v.toLocaleString(locale, { maximumFractionDigits: 1 })}
            </span>
          ))}
        </div>

        {/* Plot */}
        <div className="relative flex-1 min-w-0" style={{ height: CHART_HEIGHT }} onMouseLeave={() => setHover(null)}>
          {gridValues.map(v => (
            <div
              key={v}
              className={`absolute inset-x-0 border-t ${v === 0 ? "border-gray-300" : "border-gray-100"}`}
              style={{ top: `${(1 - v / max) * 100}%` }}
            />
          ))}

          <div className="absolute inset-0 flex items-end" style={{ gap }}>
            {buckets.map((b, i) => {
              const h = (b.points / max) * CHART_HEIGHT;
              return (
                <div
                  key={b.date}
                  className="relative flex-1 h-full flex items-end cursor-default"
                  onMouseEnter={() => setHover(i)}
                  onTouchStart={() => setHover(i)}
                  aria-label={`${formatDate(b.date)}: ${b.points} ${t("points")}, ${b.rewards} ${t("rewardsShort")}`}
                  role="img"
                >
                  <div
                    className={`w-full rounded-t-[4px] transition-colors ${hover === i ? "bg-brand-800" : "bg-brand-500"}`}
                    style={{ height: b.points > 0 ? Math.max(h, 2) : 0 }}
                  />
                </div>
              );
            })}
          </div>

          {/* Tooltip */}
          {hover !== null && buckets[hover] && (
            <div
              className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg whitespace-nowrap"
              style={{
                left: `${Math.min(Math.max(((hover + 0.5) / buckets.length) * 100, 12), 88)}%`,
                top: Math.max(CHART_HEIGHT - (buckets[hover].points / max) * CHART_HEIGHT - 6, 0),
              }}
            >
              <p className="font-semibold">{formatDate(buckets[hover].date)}</p>
              <p className="text-gray-300 tabular-nums">
                {buckets[hover].points.toLocaleString(locale)} {t("points")} · {buckets[hover].rewards.toLocaleString(locale)} {t("rewardsShort")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* X axis labels */}
      <div className="relative ml-12 mt-1.5 h-4 text-[10px] text-gray-400">
        {tickIndexes.map(i => (
          <span
            key={i}
            className="absolute whitespace-nowrap"
            style={
              i === 0 ? { left: 0 }
                : i === buckets.length - 1 ? { right: 0 }
                : { left: `${((i + 0.5) / buckets.length) * 100}%`, transform: "translateX(-50%)" }
            }
          >
            {formatDate(buckets[i].date)}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ActivityBarChart;
